import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
} from "react";
import styled from "styled-components";
import { useAuth } from "../context/AuthContext";
import {
  doc,
  getDoc,
  updateDoc,
  Timestamp,
  onSnapshot,
  serverTimestamp,
  addDoc,
  collection,
  runTransaction,
} from "firebase/firestore";
import { db } from "../firebase";
import { trackProductEvent } from "../lib/analytics";

import {
  ArrowRight,
  Lock,
  Target,
  Calendar,
  ChevronDown,
  CheckCircle,
  Loader,
  Sparkles,
  Trophy,
  Clock3,
  Layers3,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";

const Page = styled.div`
  min-height: 100vh;
  padding: 120px 20px 80px;
  background:
    radial-gradient(820px at 10% 0%, rgba(245, 158, 11, 0.12), transparent 60%),
    radial-gradient(880px at 90% 6%, rgba(56, 189, 248, 0.12), transparent 62%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;

const Header = styled.div`
  max-width: 1100px;
  margin: 0 auto 40px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 40px;
  font-weight: 1000;
  line-height: 1.05;
  color: #f8fafc;
`;

const Sub = styled.p`
  margin-top: 12px;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.7;
  max-width: 760px;
`;

const HeaderShell = styled.div`
  padding: 28px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(245, 158, 11, 0.12), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.42);

  @media (max-width: 768px) {
    padding: 22px 18px;
    border-radius: 24px;
  }
`;

const HeroTop = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(280px, 0.95fr);
  gap: 20px;
  align-items: stretch;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const HeroCopy = styled.div`
  min-width: 0;
`;

const HeroEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const FocusCard = styled.div`
  padding: 22px;
  border-radius: 24px;
  background:
    linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(56, 189, 248, 0.08)),
    rgba(15, 23, 42, 0.78);
  border: 1px solid rgba(245, 158, 11, 0.18);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
`;

const FocusLabel = styled.div`
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #fcd34d;
`;

const FocusTitle = styled.div`
  margin-top: 10px;
  color: #f8fafc;
  font-size: 22px;
  font-weight: 1000;
  line-height: 1.2;
`;

const FocusMeta = styled.div`
  margin-top: 16px;
  display: grid;
  gap: 10px;
`;

const FocusMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 16px;
  background: rgba(2, 6, 23, 0.48);
  border: 1px solid rgba(148, 163, 184, 0.12);
  color: #cbd5e1;
  font-size: 13px;
  font-weight: 700;
`;

const StatsGrid = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 920px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  padding: 18px;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.12);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
`;

const StatLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 800;
`;

const StatValue = styled.div`
  margin-top: 10px;
  color: #f8fafc;
  font-size: 28px;
  line-height: 1;
  font-weight: 1000;
`;

const StatHint = styled.div`
  margin-top: 8px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
`;

const ActionPanel = styled.div`
  margin-top: 20px;
  padding: 18px;
  border-radius: 24px;
  background: rgba(2, 6, 23, 0.5);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const ActionTitle = styled.div`
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const PhaseCard = styled.div`
  max-width: 1100px;
  margin: 0 auto 24px;
  padding: 24px;
  border-radius: 28px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.76), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.08), transparent 34%);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  border: 1px solid rgba(148, 163, 184, 0.12);
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.42),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
  transition: transform 0.35s ease, box-shadow 0.35s ease,
    border-color 0.35s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 36px 90px rgba(0, 0, 0, 0.46),
      inset 0 1px 0 rgba(255, 255, 255, 0.08);
    border-color: rgba(56, 189, 248, 0.3);
  }
`;

const PhaseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  cursor: pointer;
`;

const PhaseTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 900;
  color: #f8fafc;
`;

const PhaseKicker = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 11px;
  border-radius: 999px;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.18);
  color: #fbbf24;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const PhaseMeta = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.14);
  color: #cbd5e1;
  font-size: 12px;
  font-weight: 800;
`;

const WeekCard = styled.div`
  margin-top: 16px;
  border-radius: 22px;
  padding: 18px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.72), rgba(2, 6, 23, 0.58)),
    rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.12);

  transition: transform 0.25s ease, box-shadow 0.25s ease,
    border-color 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.32);
    border-color: rgba(56, 189, 248, 0.22);
  }
`;

const WeekHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  cursor: pointer;
`;

const WeekTitle = styled.h4`
  margin: 0;
  font-weight: 900;
  color: #f8fafc;
`;

const WeekMeta = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const DayCard = styled(motion.div)`
  margin-top: 14px;
  padding: 18px;
  border-radius: 20px;
  background: ${({ status, theme }) =>
    status === "today"
      ? "linear-gradient(135deg, rgba(245,158,11,0.18), rgba(56,189,248,0.08))"
      : "rgba(15, 23, 42, 0.78)"};
  border: 1px solid
    ${({ status }) =>
      status === "today"
        ? "rgba(245, 158, 11, 0.28)"
        : "rgba(148, 163, 184, 0.12)"};

  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.35);
  }
`;

const DayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  cursor: pointer;
`;

const DayTitleWrap = styled.div`
  min-width: 0;
`;

const DayTitle = styled.strong`
  color: #f8fafc;
  font-size: 15px;
  line-height: 1.5;
`;

const DayMetaRow = styled.div`
  margin-top: 8px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const DayStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ $tone }) =>
    $tone === "today"
      ? "#fde68a"
      : $tone === "missed"
      ? "#fecaca"
      : $tone === "done"
      ? "#bbf7d0"
      : "#cbd5e1"};
  background: ${({ $tone }) =>
    $tone === "today"
      ? "rgba(245, 158, 11, 0.16)"
      : $tone === "missed"
      ? "rgba(239, 68, 68, 0.14)"
      : $tone === "done"
      ? "rgba(34, 197, 94, 0.14)"
      : "rgba(148, 163, 184, 0.12)"};
  border: 1px solid
    ${({ $tone }) =>
      $tone === "today"
        ? "rgba(245, 158, 11, 0.28)"
        : $tone === "missed"
        ? "rgba(239, 68, 68, 0.24)"
        : $tone === "done"
        ? "rgba(34, 197, 94, 0.24)"
        : "rgba(148, 163, 184, 0.16)"};
`;

const CompleteBtn = styled.button`
  margin-top: 12px;
  padding: 10px 16px;
  border-radius: 999px;

  font-size: 14px;
  font-weight: 900;

  border: none;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  color: #fff;

  background: ${({ done, disabled }) =>
    disabled
      ? "linear-gradient(135deg, #64748b, #475569)" // disabled gray-blue
      : done
      ? "linear-gradient(135deg, #22c55e, #16a34a)" // completed green
      : "linear-gradient(135deg, #3b82f6, #2563eb)"}; // active blue

  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  transition: all 0.25s ease;

  &:hover {
    ${({ disabled }) =>
      !disabled &&
      `
        transform: translateY(-2px);
        box-shadow: 0 10px 24px rgba(59, 130, 246, 0.35);
      `}
  }

  &:active {
    ${({ disabled }) =>
      !disabled &&
      `
        transform: scale(0.96);
      `}
  }
`;

const Pill = styled.span`
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  background: #0f172a;
  border: 1px solid #1e293b;
`;

const SectionTitle = styled.div`
  margin-top: 14px;
  font-weight: 900;
  font-size: 13px;
  text-transform: uppercase;
  opacity: 0.7;
`;

const EditorBtn = styled.button`
  margin-top: 14px;
  padding: 10px 16px;
  margin-left: 10px;
  border-radius: 999px;

  display: inline-flex;
  align-items: center;
  gap: 8px;

  font-size: 13px;
  font-weight: 900;

  color: ${({ theme }) => theme.colors.primary};
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.12),
    rgba(59, 130, 246, 0.04)
  );

  border: 1px solid rgba(59, 130, 246, 0.35);
  cursor: pointer;

  transition: all 0.25s ease;

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(59, 130, 246, 0.22),
      rgba(59, 130, 246, 0.08)
    );
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(59, 130, 246, 0.25);
  }

  &:active {
    transform: scale(0.96);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;
const DashboardBtn = styled.button`
  padding: 10px 18px;
  border-radius: 999px;
  font-weight: 900;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.primary},
    ${({ theme }) => theme.colors.accent}
  );
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 30px rgba(59, 130, 246, 0.35);
  }
`;
const QuestionCard = styled.div`
  margin-top: 12px;
  padding: 16px;
  border-radius: 14px;
  background: linear-gradient(
    135deg,
    rgba(99, 102, 241, 0.12),
    rgba(99, 102, 241, 0.04)
  );
  border: 1px solid rgba(99, 102, 241, 0.35);
`;

const QuestionTitle = styled.div`
  font-weight: 900;
  font-size: 14px;
  margin-bottom: 6px;
`;

const QuestionMeta = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 12px;
  opacity: 0.85;
  margin-top: 6px;
`;

const QuestionPill = styled.span`
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(99, 102, 241, 0.18);
  border: 1px solid rgba(99, 102, 241, 0.35);
  font-weight: 800;
`;

const EmptyRoadmapState = styled.div`
  max-width: 920px;
  margin: 140px auto 80px;
  padding: 40px 32px;
  border-radius: 32px;
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.9), rgba(2, 6, 23, 0.86)),
    linear-gradient(135deg, rgba(245, 158, 11, 0.08), rgba(56, 189, 248, 0.08));
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 28px 90px rgba(0, 0, 0, 0.34);
  text-align: center;

  @media (max-width: 640px) {
    margin: 112px 14px 48px;
    padding: 28px 20px;
    border-radius: 24px;
  }
`;

const EmptyRoadmapEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.16);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const EmptyRoadmapTitle = styled.h2`
  margin-top: 18px;
  color: #f8fafc;
  font-size: clamp(30px, 4vw, 44px);
  line-height: 1.06;
  font-weight: 1000;
`;

const EmptyRoadmapText = styled.p`
  max-width: 640px;
  margin: 14px auto 0;
  color: #94a3b8;
  font-size: 16px;
  line-height: 1.8;
`;

const EmptyRoadmapCTA = styled.button`
  margin-top: 24px;
  min-height: 52px;
  padding: 0 22px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 900;
  color: #04111f;
  background: linear-gradient(135deg, #f59e0b, #facc15);
  box-shadow: 0 20px 44px rgba(245, 158, 11, 0.26);
`;

const UpgradeBtn = styled.button`
  margin-top: 18px;
  width: 100%;
  padding: 12px 16px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 14px;
  border: none;
  cursor: pointer;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  background: linear-gradient(90deg, #6366f1, #4f46e5);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 30px rgba(99, 102, 241, 0.45);
  }
`;
const ActionRow = styled.div`
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;
const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;

  padding: 10px 14px;
  font-size: 14px;
  font-weight: 500;

  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: rgba(15, 23, 42, 0.6);
  color: #e5e7eb;

  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(30, 41, 59, 0.9);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
  }
`;
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);

  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 420px;

  /* Glass effect */
  background: rgba(2, 6, 23, 0.65);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);

  border-radius: 20px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.6),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);

  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const ModalHeader = styled.div`
  padding: 16px 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }
`;
const ModalFooter = styled.div`
  padding: 16px 18px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  border-top: 1px solid rgba(148, 163, 184, 0.1);
`;

const Cancel = styled.button`
  background: transparent;
  color: #94a3b8;
  border: none;
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    color: #e5e7eb;
  }
`;

const Save = styled.button`
  background: linear-gradient(90deg, #38bdf8, #6366f1);
  color: #020617;
  border: none;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const IconCircle = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  /* Glass background */
  background: rgba(99, 102, 241, 0.15);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  /* Icon color */
  color: #e5e7eb;
  font-size: 18px;

  border: 1px solid rgba(99, 102, 241, 0.35);
`;

const CloseBtn = styled.button`
  background: transparent;
  border: none;
  color: #94a3b8;
  font-size: 18px;
  cursor: pointer;

  &:hover {
    color: #e5e7eb;
  }
`;
const ModalBody = styled.div`
  padding: 18px;
`;

const Label = styled.div`
  font-size: 14px;
  color: #cbd5f5;
  margin-bottom: 8px;
`;

const TimeInput = styled.input`
  background: rgba(255, 255, 255, 0.04);
  color: #f8fafc;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 10px 12px;

  /* 🔑 THIS FIXES BLACK ICON */
  color-scheme: dark;

  &:focus {
    outline: none;
    border-color: #6366f1;
  }
`;

const Hint = styled.p`
  margin-top: 10px;
  font-size: 12px;
  color: #94a3b8;
`;
const MetaRow = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 10px;

  font-size: 13px;
  font-weight: 500;
  color: #cbd5f5;

  span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: rgba(15, 23, 42, 0.6);
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(148, 163, 184, 0.18);
  }
`;
const SettingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-radius: 14px;

  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);

  margin-bottom: 12px;
`;

const SettingText = styled.div`
  h4 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
  }
  p {
    margin-top: 4px;
    font-size: 13px;
    opacity: 0.7;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
  margin: 14px 0;
`;

const InfoBox = styled.div`
  margin-top: 18px;
  font-size: 13px;
  opacity: 0.65;
  line-height: 1.6;
`;
/* ================= SKELETON ================= */

const shimmer = `
  background: linear-gradient(
    90deg,
    rgba(255,255,255,0.04) 25%,
    rgba(255,255,255,0.12) 37%,
    rgba(255,255,255,0.04) 63%
  );
  background-size: 400% 100%;
  animation: shimmer 1.4s ease infinite;
`;

const SkeletonBox = styled.div`
  height: ${({ h }) => h || 14}px;
  width: ${({ w }) => w || "100%"};
  border-radius: 8px;
  ${shimmer}
`;

const SkeletonHeaderShell = styled.div`
  max-width: 1100px;
  margin: 0 auto 40px;
  padding: 28px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.1), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.42);
`;

const SkeletonHero = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(280px, 0.95fr);
  gap: 20px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const SkeletonPanel = styled.div`
  padding: 22px;
  border-radius: 24px;
  background: rgba(15, 23, 42, 0.68);
  border: 1px solid rgba(148, 163, 184, 0.1);
`;

const SkeletonStatsGrid = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 920px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const SkeletonStatCard = styled.div`
  padding: 18px;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const SkeletonActionPanel = styled.div`
  margin-top: 20px;
  padding: 18px;
  border-radius: 24px;
  background: rgba(2, 6, 23, 0.5);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const SkeletonPhase = styled.div`
  max-width: 1100px;
  margin: 0 auto 24px;
  padding: 24px;
  border-radius: 28px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.76), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.08), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.12);
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.32);
`;

const SkeletonWeek = styled.div`
  margin-top: 18px;
  padding: 18px;
  border-radius: 22px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.72), rgba(2, 6, 23, 0.58)),
    rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.1);
`;

const SkeletonDay = styled.div`
  margin-top: 12px;
  padding: 18px;
  border-radius: 20px;
  background: rgba(15, 23, 42, 0.78);
  border: 1px solid rgba(148, 163, 184, 0.1);
`;

const SkeletonRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const SkeletonCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const MentorCTA = styled.div`
  margin-top: 20px;
  padding: 18px 20px;
  border-radius: 18px;

  background: linear-gradient(
    135deg,
    rgba(99, 102, 241, 0.22),
    rgba(99, 102, 241, 0.08)
  );

  border: 1px solid rgba(99, 102, 241, 0.45);

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  box-shadow: 0 20px 40px rgba(99, 102, 241, 0.25);
`;

const MentorText = styled.div`
  font-size: 15px;
  font-weight: 700;
  line-height: 1.5;

  span {
    display: block;
    font-size: 13px;
    opacity: 0.75;
    font-weight: 500;
    margin-top: 4px;
  }
`;

const MentorBtn = styled.button`
  padding: 12px 18px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 14px;

  border: none;
  cursor: pointer;

  background: linear-gradient(90deg, #6366f1, #4f46e5);
  color: #fff;

  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 12px 30px rgba(99, 102, 241, 0.45);
  }
`;

/* ================= HELPERS ================= */

const formatDateKey = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDayStatusByIndex = (index, startedAt, completedDays) => {
  if (!startedAt) return "upcoming";

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const base = startedAt.toDate ? startedAt.toDate() : new Date(startedAt);

  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  d.setDate(d.getDate() + index);
  d.setHours(0, 0, 0, 0);

  const key = formatDateKey(d);

  if (completedDays[key]) return "done";
  if (d.getTime() === today.getTime()) return "today";
  if (d < today) return "missed";
  return "upcoming";
};

/* 🔥 ABSOLUTE DAY INDEX — CORE LOGIC */
const getAbsoluteDayIndex = (phases, pIdx, wIdx, dIdx) => {
  let count = 0;

  for (let p = 0; p < pIdx; p++) {
    phases[p].weeks.forEach((w) => (count += w.days.length));
  }

  for (let w = 0; w < wIdx; w++) {
    count += phases[pIdx].weeks[w].days.length;
  }

  return count + dIdx;
};
const getDateFromAbsoluteIndex = (startedAt, abs) => {
  const base = startedAt.toDate ? startedAt.toDate() : new Date(startedAt);

  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  d.setDate(d.getDate() + abs); // ✅ only once
  return d;
};

const getDayKey = (phase, week, day) =>
  [phase?.id || phase?.phaseKey || phase?.phase, week?.id || week?.week, day?.id || day?.day]
    .filter(Boolean)
    .join("-");

const getWeekKey = (phase, week) =>
  [phase?.id || phase?.phaseKey || phase?.phase, week?.id || week?.week]
    .filter(Boolean)
    .join("-");

// const buildRuntimeRoadmapFromFirestore = async (roadmapRef) => {
//   if (!roadmapRef?.phases?.length) return [];

//   const phases = [];

//   for (const refPhase of roadmapRef.phases) {
//     const phaseSnap = await getDoc(doc(db, "roadmap_phases", refPhase.phaseId));
//     if (!phaseSnap.exists()) continue;

//     const phase = {
//       id: phaseSnap.id,
//       ...phaseSnap.data(),
//       weeks: [],
//     };

//     for (const refWeek of refPhase.weeks) {
//       const weekSnap = await getDoc(doc(db, "roadmap_weeks", refWeek.weekId));
//       if (!weekSnap.exists()) continue;

//       const week = {
//         id: weekSnap.id,
//         ...weekSnap.data(),
//         days: [],
//       };

//       for (const refDay of refWeek.days) {
//         const daySnap = await getDoc(doc(db, "roadmap_days", refDay.dayId));
//         if (daySnap.exists()) {
//           week.days.push({
//             id: daySnap.id,
//             ...daySnap.data(),
//           });
//         }
//       }

//       week.days.sort((a, b) => a.day - b.day);
//       phase.weeks.push(week);
//     }

//     phase.weeks.sort((a, b) => a.week - b.week);
//     phases.push(phase);
//   }

//   return phases.sort((a, b) => a.order - b.order);
// };

const buildRuntimeRoadmapFromFirestore = async (roadmapRef) => {
  if (!roadmapRef?.phases?.length) return [];

  const phasePromises = roadmapRef.phases.map(async (refPhase) => {
    const phaseSnap = await getDoc(doc(db, "roadmap_phases", refPhase.phaseId));
    if (!phaseSnap.exists()) return null;

    const phase = {
      id: phaseSnap.id,
      ...phaseSnap.data(),
      weeks: [],
    };

    const weekPromises = refPhase.weeks.map(async (refWeek) => {
      const weekSnap = await getDoc(doc(db, "roadmap_weeks", refWeek.weekId));
      if (!weekSnap.exists()) return null;

      const week = {
        id: weekSnap.id,
        ...weekSnap.data(),
        days: [],
      };

      const dayPromises = refWeek.days.map(async (refDay) => {
        const daySnap = await getDoc(doc(db, "roadmap_days", refDay.dayId));
        if (!daySnap.exists()) return null;

        return {
          id: daySnap.id,
          ...daySnap.data(),
        };
      });

      const days = (await Promise.all(dayPromises))
        .filter(Boolean)
        .sort((a, b) => a.day - b.day);

      week.days = days;
      return week;
    });

    const weeks = (await Promise.all(weekPromises))
      .filter(Boolean)
      .sort((a, b) => a.week - b.week);

    phase.weeks = weeks;
    return phase;
  });

  const phases = (await Promise.all(phasePromises))
    .filter(Boolean)
    .sort((a, b) => a.order - b.order);

  return phases;
};

/* ================= COMPONENT ================= */

export default function MyRoadmapPage() {
  const { user } = useAuth();
  const [roadmapMeta, setRoadmapMeta] = useState(null);
  const [completedDays, setCompletedDays] = useState({});
  const [openPhase, setOpenPhase] = useState(null);
  const [openWeek, setOpenWeek] = useState(null);
  const [openDay, setOpenDay] = useState(null);
  const [showShareProgress, setShowShareProgress] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [reflection, setReflection] = useState("");
  const [sharing, setSharing] = useState(false);
  const [completingDayId, setCompletingDayId] = useState(null);
  const navigate = useNavigate();
  // const [startedAt, setStartedAt] = useState(null);
  const [streak, setStreak] = useState(0);
  const [showReminder, setShowReminder] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [isPro, setIsPro] = useState(false);
  const [emailReminderEnabled, setEmailReminderEnabled] = useState(true);
  const [calendarReminderEnabled, setCalendarReminderEnabled] = useState(false);
  const [calendarTime, setCalendarTime] = useState("07:30");
  const [saving, setSaving] = useState(false);
  const [filteredPhases, setFilteredPhases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCalendarSuccess, setShowCalendarSuccess] = useState(false);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const hasAutoExpandedCurrentRef = useRef(false);
  const getGoogleCalendarCallbackUrl = () => {
    return "https://us-central1-skilll-dev.cloudfunctions.net/googleCalendarCallback";
  };

  const getGoogleAuthUrl = () => {
    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: getGoogleCalendarCallbackUrl(),
      response_type: "code",
      access_type: "offline",
      prompt: "consent",
      include_granted_scopes: "true",
      scope: "https://www.googleapis.com/auth/calendar.events",
      state: user.uid,
    });

    return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };
  const scrollToWeek = (weekKey) => {
    if (!weekKey) return;

    const el = document.querySelector(`[data-week-key="${weekKey}"]`);
    if (!el) return;

    const headerOffset = 120;
    const top =
      el.getBoundingClientRect().top + window.scrollY - headerOffset;

    window.scrollTo({
      top: Math.max(top, 0),
      behavior: "smooth",
    });
  };

  const startedAt = useMemo(() => {
    return roadmapMeta?.startedAt ? roadmapMeta.startedAt.toDate() : null;
  }, [roadmapMeta]);

  useEffect(() => {
    if (!user) return;

    const unsub = onSnapshot(doc(db, "users", user.uid), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setIsPro(data.plan === "PRO");
        setCalendarConnected(
          !!data.calendarConnected && !!data.calendarRefreshToken
        );
      }
    });

    return unsub;
  }, [user]);
  useEffect(() => {
    if (!user?.uid) return;
    const loadReminderSettings = async () => {
      try {
        const snap = await getDoc(doc(db, "roadmaps", user.uid));
        if (!snap.exists()) return;

        const data = snap.data();

        setEmailReminderEnabled(data.emailReminderEnabled ?? false);

        setCalendarReminderEnabled(data.calendarReminderEnabled ?? false);

        setCalendarTime(data.calendarReminderTime || "07:30");
      } catch (err) {
        console.error("Failed to load reminder settings", err);
      }
    };

    if (showReminder) {
      loadReminderSettings();
    }
  }, [showReminder, user?.uid]);

  useEffect(() => {
    if (!user) return;

    const loadRoadmap = async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, "roadmaps", user.uid));

        if (!snap.exists()) {
          setRoadmapMeta(null);
          setCompletedDays({});
          setFilteredPhases([]);
          return;
        }

        const data = snap.data();

        setRoadmapMeta(data);
        setCompletedDays(data.completedDays || {});

        const runtime = await buildRuntimeRoadmapFromFirestore(data.roadmapRef);
        setFilteredPhases(runtime);
      } finally {
        setLoading(false);
      }
    };

    loadRoadmap();
  }, [user]);

  useEffect(() => {
    if (!roadmapMeta?.roadmapRef) return;

    const load = async () => {
      const data = await buildRuntimeRoadmapFromFirestore(
        roadmapMeta.roadmapRef
      );
      setFilteredPhases(data);
    };

    load();
  }, [roadmapMeta?.roadmapRef]);

  const getTotalDaysFromRoadmapRef = (roadmapRef) => {
    if (!roadmapRef?.phases) return 0;

    let total = 0;

    roadmapRef.phases.forEach((phase) => {
      phase.weeks.forEach((week) => {
        total += week.days?.length || 0;
      });
    });

    return total;
  };
  const calculateProgressPercent = (roadmapRef, completedDays = {}) => {
    const totalDays = getTotalDaysFromRoadmapRef(roadmapRef);
    const completedCount = Object.keys(completedDays).length;

    if (!totalDays) return 0;

    return Math.round((completedCount / totalDays) * 100);
  };

  const calculateDayStreak = (completedDays = {}) => {
    let streak = 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 365; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);

      const key = formatDateKey(d);

      if (completedDays[key]) {
        streak++;
      } else {
        break; // 🔥 yahin streak toot ta hai
      }
    }

    return streak;
  };
  const stats = useMemo(() => {
    if (!roadmapMeta?.roadmapRef) {
      return {
        totalDays: 0,
        completed: 0,
        remaining: 0,
        percent: 0,
        streak: 0,
      };
    }

    const totalDays = getTotalDaysFromRoadmapRef(roadmapMeta.roadmapRef);
    const completed = Object.keys(completedDays || {}).length;

    return {
      totalDays,
      completed,
      remaining: Math.max(totalDays - completed, 0),
      percent: calculateProgressPercent(roadmapMeta.roadmapRef, completedDays),
      streak: calculateDayStreak(completedDays),
    };
  }, [roadmapMeta?.roadmapRef, completedDays]);

  useEffect(() => {
    if (!startedAt || !filteredPhases.length || hasAutoExpandedCurrentRef.current)
      return;

    const start = new Date(startedAt);
    start.setHours(0, 0, 0, 0);
    const todayKey = formatDateKey(new Date());

    let dayCounter = 0;

    for (let pIdx = 0; pIdx < filteredPhases.length; pIdx++) {
      const phase = filteredPhases[pIdx];

      for (const week of phase.weeks) {
        for (const day of week.days) {
          const d = new Date(start);
          d.setDate(start.getDate() + dayCounter);

          if (formatDateKey(d) === todayKey) {
            hasAutoExpandedCurrentRef.current = true;
            setOpenPhase(pIdx);
            setOpenWeek(getWeekKey(phase, week));
            setOpenDay(getDayKey(phase, week, day));
            return;
          }

          dayCounter++;
        }
      }
    }
  }, [startedAt, filteredPhases]);

  useEffect(() => {
    if (!openWeek) return;

    const t = window.setTimeout(() => {
      scrollToWeek(openWeek);
    }, 120);

    return () => window.clearTimeout(t);
  }, [openPhase, openWeek]);

  const startJourney = async () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    await updateDoc(doc(db, "roadmaps", user.uid), {
      startedAt: Timestamp.fromDate(today),
    });

    setRoadmapMeta({ ...roadmapMeta, startedAt: Timestamp.fromDate(today) });

    void trackProductEvent(
      "roadmap_journey_started",
      {
        roadmapMetaId: roadmapMeta?.roadmapMetaId || null,
        role: roadmapMeta?.direction?.role || null,
      },
      { user }
    );
  };

  const markComplete = async (date, dayMeta) => {
    const key = formatDateKey(date);
    const roadmapRef = doc(db, "roadmaps", user.uid);
    const userRef = doc(db, "users", user.uid);
    let alreadyCompleted = false;

    try {
      setCompletingDayId(dayMeta.id);

      await runTransaction(db, async (transaction) => {
        const roadmapSnap = await transaction.get(roadmapRef);
        const userSnap = await transaction.get(userRef);

        if (!roadmapSnap.exists()) {
          throw new Error("Roadmap not found");
        }

        const roadmapData = roadmapSnap.data() || {};
        if (roadmapData.completedDays?.[key]) {
          alreadyCompleted = true;
          return;
        }

        const userData = userSnap.exists() ? userSnap.data() : {};
        const currentCoins = Number(userData.coins || 0);
        const currentCompleted = Number(userData.totalCompletedDays || 0);

        transaction.update(roadmapRef, {
          [`completedDays.${key}`]: true,
        });

        transaction.set(
          userRef,
          {
            coins: currentCoins + 100,
            totalCompletedDays: currentCompleted + 1,
            lastCompletedAt: serverTimestamp(),
          },
          { merge: true }
        );
      });

      if (alreadyCompleted) return;

      setCompletedDays((p) => ({ ...p, [key]: true }));
      setShareData({
        completedAt: date,
        dayId: dayMeta.id,
        dayTitle: dayMeta.title || dayMeta.topic,
        dayNumber: dayMeta.day,
        roadmapId: roadmapMeta.roadmapMetaId,
        roadmapTitle: roadmapMeta.direction?.role,
      });

      setReflection("");
      setShowShareProgress(true);
    } finally {
      setCompletingDayId(null);
    }
  };

  if (loading && !roadmapMeta) {
    return (
      <Page>
        <RoadmapSkeleton />
      </Page>
    );
  }

  if (!roadmapMeta) {
    return (
      <Page>
        <EmptyRoadmapState>
          <EmptyRoadmapEyebrow>
            <Sparkles size={14} />
            Roadmap not found
          </EmptyRoadmapEyebrow>
          <EmptyRoadmapTitle>You do not have a roadmap yet</EmptyRoadmapTitle>
          <EmptyRoadmapText>
            Create your roadmap first so we can turn your goal into a clear
            phase, week, and day execution system.
          </EmptyRoadmapText>
          <EmptyRoadmapCTA onClick={() => navigate("/career-clarity-home")}>
            <ArrowRight size={16} />
            Create My Roadmap
          </EmptyRoadmapCTA>
        </EmptyRoadmapState>
      </Page>
    );
  }
  const saveReminderTime = async () => {
    try {
      setSaving(true);

      await updateDoc(doc(db, "roadmaps", user.uid), {
        // Email reminder (fixed time, backend controlled)
        emailReminderEnabled,

        // Calendar reminder (user controlled)
        calendarReminderEnabled,
        calendarReminderTime: calendarReminderEnabled
          ? calendarTime || "07:30"
          : null,

        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        updatedAt: serverTimestamp(),
      });

      setShowReminder(false);
      if (calendarReminderEnabled && calendarConnected) {
        setShowCalendarSuccess(true);
      } else if (calendarReminderEnabled && !calendarConnected) {
        window.location.href = getGoogleAuthUrl();
      }
    } catch (err) {
      console.error("Failed to save reminder settings", err);
      alert("Failed to save reminder. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handlePremiumNav = (path) => {
    if (!isPro) {
      navigate("/pricing");
      return;
    }
    navigate(path);
  };
  const Toggle = ({ checked, onChange }) => {
    return (
      <label
        style={{
          position: "relative",
          display: "inline-block",
          width: 44,
          height: 24,
        }}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          style={{ opacity: 0, width: 0, height: 0 }}
        />
        <span
          style={{
            position: "absolute",
            cursor: "pointer",
            inset: 0,
            backgroundColor: checked ? "#4f46e5" : "#374151",
            borderRadius: 999,
            transition: "0.3s",
          }}
        >
          <span
            style={{
              position: "absolute",
              height: 18,
              width: 18,
              left: checked ? 22 : 3,
              top: 3,
              backgroundColor: "#fff",
              borderRadius: "50%",
              transition: "0.3s",
            }}
          />
        </span>
      </label>
    );
  };
  const FREE_WEEK_LIMIT = 1;

  const isWeekLocked = (weekNumber, isPro) => {
    if (isPro) return false;
    return weekNumber > FREE_WEEK_LIMIT;
  };
  function RoadmapSkeleton() {
    return (
      <>
        <SkeletonHeaderShell>
          <SkeletonHero>
            <div>
              <SkeletonBox h={34} w="210px" style={{ borderRadius: 999 }} />
              <div style={{ marginTop: 16 }}>
                <SkeletonBox h={44} w="58%" />
              </div>
              <div style={{ marginTop: 12 }}>
                <SkeletonBox h={14} w="90%" />
              </div>
              <div style={{ marginTop: 10 }}>
                <SkeletonBox h={14} w="76%" />
              </div>
            </div>

            <SkeletonPanel>
              <SkeletonBox h={12} w="110px" />
              <div style={{ marginTop: 14 }}>
                <SkeletonBox h={34} w="82%" />
              </div>
              <div style={{ marginTop: 16 }}>
                <SkeletonBox h={40} w="100%" style={{ borderRadius: 16 }} />
              </div>
              <div style={{ marginTop: 10 }}>
                <SkeletonBox h={40} w="100%" style={{ borderRadius: 16 }} />
              </div>
              <div style={{ marginTop: 10 }}>
                <SkeletonBox h={40} w="100%" style={{ borderRadius: 16 }} />
              </div>
            </SkeletonPanel>
          </SkeletonHero>

          <SkeletonStatsGrid>
            {Array.from({ length: 4 }).map((_, index) => (
              <SkeletonStatCard key={index}>
                <SkeletonBox h={12} w="88px" />
                <div style={{ marginTop: 12 }}>
                  <SkeletonBox h={28} w="58px" />
                </div>
                <div style={{ marginTop: 10 }}>
                  <SkeletonBox h={10} w="94%" />
                </div>
                <div style={{ marginTop: 8 }}>
                  <SkeletonBox h={10} w="72%" />
                </div>
              </SkeletonStatCard>
            ))}
          </SkeletonStatsGrid>

          <SkeletonActionPanel>
            <SkeletonBox h={12} w="130px" />
            <SkeletonRow style={{ marginTop: 16, flexWrap: "wrap" }}>
              <SkeletonBox h={42} w="150px" style={{ borderRadius: 999 }} />
              <SkeletonBox h={42} w="168px" style={{ borderRadius: 999 }} />
              <SkeletonBox h={42} w="150px" style={{ borderRadius: 999 }} />
            </SkeletonRow>
          </SkeletonActionPanel>
        </SkeletonHeaderShell>

        {[1, 2].map((p) => (
          <SkeletonPhase key={p}>
            <SkeletonRow>
              <SkeletonBox h={28} w="96px" style={{ borderRadius: 999 }} />
            </SkeletonRow>

            <SkeletonRow style={{ marginTop: 16, justifyContent: "space-between" }}>
              <SkeletonBox h={22} w="42%" />
              <SkeletonBox h={16} w={24} />
            </SkeletonRow>

            <SkeletonRow style={{ marginTop: 10 }}>
              <SkeletonBox h={32} w={120} style={{ borderRadius: 999 }} />
              <SkeletonBox h={32} w={92} style={{ borderRadius: 999 }} />
            </SkeletonRow>

            {[1, 2].map((w) => (
              <SkeletonWeek key={w}>
                <SkeletonRow style={{ justifyContent: "space-between" }}>
                  <SkeletonBox h={18} w="45%" />
                  <SkeletonBox h={14} w={20} />
                </SkeletonRow>

                <SkeletonRow style={{ marginTop: 12 }}>
                  <SkeletonBox h={30} w={132} style={{ borderRadius: 999 }} />
                  <SkeletonBox h={30} w={156} style={{ borderRadius: 999 }} />
                </SkeletonRow>

                {[1, 2, 3].map((d) => (
                  <SkeletonDay key={d}>
                    <SkeletonCol>
                      <SkeletonRow style={{ justifyContent: "space-between" }}>
                        <SkeletonBox h={16} w="68%" />
                        <SkeletonBox h={16} w={18} />
                      </SkeletonRow>
                      <SkeletonRow style={{ marginTop: 10 }}>
                        <SkeletonBox h={26} w={84} style={{ borderRadius: 999 }} />
                      </SkeletonRow>
                    </SkeletonCol>
                  </SkeletonDay>
                ))}
              </SkeletonWeek>
            ))}
          </SkeletonPhase>
        ))}
      </>
    );
  }

  return (
    <Page>
      <Header>
        <HeaderShell>
          <HeroTop>
            <HeroCopy>
              <HeroEyebrow>
                <Sparkles size={14} />
                Personalized Execution System
              </HeroEyebrow>
              <Title>Your Career Roadmap</Title>

              <Sub>
                This is your active path to become a{" "}
                <strong>{roadmapMeta.direction.role}</strong>. Every phase,
                week, and day is sequenced to help you make visible progress
                toward <strong>{roadmapMeta.direction.goal}</strong>.
              </Sub>
            </HeroCopy>

            <FocusCard>
              <FocusLabel>Current Focus</FocusLabel>
              <FocusTitle>
                {startedAt
                  ? `${stats.remaining} days left to finish this roadmap`
                  : "Start your roadmap and unlock your first execution day"}
              </FocusTitle>

              <FocusMeta>
                <FocusMetaRow>
                  <Layers3 size={16} />
                  {filteredPhases.length} phases mapped into daily execution
                </FocusMetaRow>
                <FocusMetaRow>
                  <Clock3 size={16} />
                  {startedAt
                    ? `You are ${stats.percent}% through the system`
                    : "Your journey has not started yet"}
                </FocusMetaRow>
                <FocusMetaRow>
                  <Trophy size={16} />
                  {startedAt
                    ? `${stats.streak} day streak in motion`
                    : "Build consistency from day one"}
                </FocusMetaRow>
              </FocusMeta>
            </FocusCard>
          </HeroTop>

          <StatsGrid>
            <StatCard>
              <StatLabel>
                <Layers3 size={14} />
                Total days
              </StatLabel>
              <StatValue>{stats.totalDays}</StatValue>
              <StatHint>Structured into phases, weeks, and daily tasks.</StatHint>
            </StatCard>

            <StatCard>
              <StatLabel>
                <CheckCircle size={14} />
                Completed
              </StatLabel>
              <StatValue>{stats.completed}</StatValue>
              <StatHint>Every finished day becomes visible proof of work.</StatHint>
            </StatCard>

            <StatCard>
              <StatLabel>
                <Trophy size={14} />
                Streak
              </StatLabel>
              <StatValue>{startedAt ? stats.streak : 0}</StatValue>
              <StatHint>Consistency builds confidence faster than intensity.</StatHint>
            </StatCard>

            <StatCard>
              <StatLabel>
                <Target size={14} />
                Progress
              </StatLabel>
              <StatValue>{startedAt ? `${stats.percent}%` : "0%"}</StatValue>
              <StatHint>Track how much of your roadmap is already complete.</StatHint>
            </StatCard>
          </StatsGrid>

          <ActionPanel>
            <ActionTitle>Roadmap Controls</ActionTitle>
            <ActionRow>
              {!startedAt && (
                <DashboardBtn onClick={startJourney}>
                  <ArrowRight size={16} />
                  Start Journey
                </DashboardBtn>
              )}

              <IconBtn
                disabled={!startedAt}
                style={{
                  opacity: !isPro ? 0.85 : 1,
                  borderColor: !isPro ? "rgba(99,102,241,0.5)" : undefined,
                }}
                onClick={() => {
                  if (!isPro) {
                    navigate("/pricing");
                    return;
                  }
                  setShowReminder(true);
                }}
              >
                {!isPro && <Lock size={14} />}
                ⏰ Reminder Settings
              </IconBtn>
              <IconBtn
                disabled={!startedAt}
                style={{
                  opacity: !isPro ? 0.85 : 1,
                  borderColor: !isPro ? "rgba(99,102,241,0.5)" : undefined,
                }}
                onClick={() => handlePremiumNav("/roadmap/calendar")}
              >
                {!isPro && <Lock size={14} />}
                📅 Calendar View
              </IconBtn>

              <DashboardBtn
                onClick={() =>
                  isPro ? navigate("/dashboard") : navigate("/pricing")
                }
              >
                {!isPro && <Lock size={16} />}
                📊 Progress Dashboard
              </DashboardBtn>

              <DashboardBtn onClick={() => navigate("/focus-without-phone")}>
                🎯 Focus Without Phone
              </DashboardBtn>
            </ActionRow>

            {!isPro && startedAt && (
              <p
                style={{
                  marginTop: 14,
                  fontSize: 13,
                  color: "#94a3b8",
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                🔓 <strong style={{ color: "#e2e8f0" }}>Go Pro</strong> to unlock:
                <span>⏰ Daily reminders</span>
                <span>📅 Calendar planning</span>
                <span>🔥 Consistency system</span>
                <span>🤖 AI mentor for every day</span>
                <span>📊 Advanced dashboard</span>
              </p>
            )}
          </ActionPanel>
        </HeaderShell>
      </Header>
      {loading && <RoadmapSkeleton />}
      {filteredPhases.map((phase, pIdx) => {
        const weeksCount = phase.weeks?.length || 0;

        return (
          <PhaseCard key={phase.id}>
            <PhaseHeader
              onClick={() => setOpenPhase(openPhase === pIdx ? null : pIdx)}
            >
              <div style={{ flex: 1 }}>
                <PhaseKicker>
                  <Sparkles size={12} />
                  Phase {pIdx + 1}
                </PhaseKicker>
                <PhaseTitle>{phase.title}</PhaseTitle>

                <PhaseMeta>
                  <MetaBadge>
                    <Calendar size={14} /> {weeksCount} weeks
                  </MetaBadge>

                  {phase.level && (
                    <MetaBadge>
                      <Target size={14} /> {phase.level}
                    </MetaBadge>
                  )}
                </PhaseMeta>
              </div>

              <ChevronDown
                size={22}
                style={{
                  transform:
                    openPhase === pIdx ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "0.25s ease",
                  opacity: 0.85,
                }}
              />
            </PhaseHeader>

            <AnimatePresence>
              {openPhase === pIdx &&
                phase.weeks.map((week, wIdx) => {
                  const weekLocked = isWeekLocked(week.week, isPro);
                  const weekKey = getWeekKey(phase, week);

                  return (
                    <WeekCard
                      key={week.id}
                      data-week-key={weekKey}
                      style={{
                        opacity: weekLocked ? 0.55 : 1,
                        cursor: weekLocked ? "not-allowed" : "pointer",
                      }}
                    >
                      <WeekHeader
                        onClick={() => {
                          if (weekLocked) return;
                          const nextWeekKey =
                            openWeek === weekKey ? null : weekKey;
                          setOpenWeek(nextWeekKey);
                        }}
                      >
                        <WeekTitle
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          Week {wIdx + 1}. {week.title}{" "}
                          {weekLocked && <Lock size={16} />}
                        </WeekTitle>

                        <ChevronDown
                          size={18}
                          style={{
                            transform:
                              openWeek === weekKey
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                            transition: "0.2s ease",
                          }}
                          />
                      </WeekHeader>

                      <WeekMeta>
                        <MetaBadge>
                          <Calendar size={14} />
                          {week.days.length} execution days
                        </MetaBadge>
                        <MetaBadge>
                          <Target size={14} />
                          {weekLocked ? "Locked for free plan" : "Ready to execute"}
                        </MetaBadge>
                      </WeekMeta>

                      {/* LOCKED STATE */}
                      {weekLocked && (
                        <div
                          style={{
                            marginTop: 14,
                            padding: 16,
                            borderRadius: 14,
                            background: "rgba(99,102,241,0.08)",
                            border: "1px solid rgba(99,102,241,0.35)",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                          }}
                        >
                          <div style={{ fontSize: 14, fontWeight: 700 }}>
                            🔒 Week {week.week} is locked
                            <div style={{ fontSize: 12, opacity: 0.75 }}>
                              Week 1 is free. Upgrade to PRO to continue.
                            </div>
                          </div>

                          <UpgradeBtn onClick={() => navigate("/pricing")}>
                            Unlock PRO
                          </UpgradeBtn>
                        </div>
                      )}

                      {/* DAYS */}
                      {openWeek === weekKey &&
                        week.days.map((day, dIdx) => {
                          const abs = getAbsoluteDayIndex(
                            filteredPhases,
                            pIdx,
                            wIdx,
                            dIdx
                          );
                          const status = getDayStatusByIndex(
                            abs,
                            startedAt,
                            completedDays
                          );
                          const date = getDateFromAbsoluteIndex(startedAt, abs);
                          const dayKey = getDayKey(phase, week, day);
                          const isOpen = openDay === dayKey;

                          return (
                            <DayCard key={day.id} status={status}>
                              <DayHeader
                                onClick={() => {
                                  if (weekLocked) {
                                    navigate("/pricing");
                                    return;
                                  }
                                  setOpenDay(isOpen ? null : dayKey);
                                }}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  cursor: "pointer",
                                  gap: 12,
                                }}
                              >
                                <DayTitleWrap>
                                  <DayTitle>
                                    Day {day.day}: {day.title || day.topic}
                                  </DayTitle>

                                  <DayMetaRow>
                                    {status === "today" && (
                                      <DayStatus $tone="today">Today</DayStatus>
                                    )}
                                    {status === "missed" && (
                                      <DayStatus $tone="missed">Missed</DayStatus>
                                    )}
                                    {status === "done" && (
                                      <DayStatus $tone="done">Completed</DayStatus>
                                    )}
                                    {status === "upcoming" && (
                                      <DayStatus $tone="upcoming">Upcoming</DayStatus>
                                    )}
                                  </DayMetaRow>
                                </DayTitleWrap>

                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                  }}
                                >
                                  {status === "done" && (
                                    <CheckCircle size={16} color="#22c55e" />
                                  )}

                                  <ChevronDown
                                    size={18}
                                    style={{
                                      transform: isOpen
                                        ? "rotate(180deg)"
                                        : "rotate(0deg)",
                                      transition: "0.2s ease",
                                    }}
                                    />
                                </div>
                              </DayHeader>

                              <AnimatePresence>
                                {isOpen && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.25 }}
                                  >
                                    {day.subTopics?.length > 0 && (
                                      <>
                                        <SectionTitle>
                                          Topics Covered
                                        </SectionTitle>

                                        <div
                                          style={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 8,
                                          }}
                                        >
                                          {day.subTopics.map((t, i) => (
                                            <Pill key={i}>{t}</Pill>
                                          ))}
                                        </div>
                                      </>
                                    )}

                                    <CompleteBtn
                                      done={status === "done"}
                                      disabled={
                                        completingDayId === day.id ||
                                        !(
                                          status === "today" ||
                                          status === "missed"
                                        )
                                      }
                                      onClick={() =>
                                        (status === "today" ||
                                          status === "missed") &&
                                        markComplete(date, day)
                                      }
                                    >
                                      {completingDayId === day.id ? (
                                        <>
                                          <Loader size={16} className="spin" />
                                          Completing...
                                        </>
                                      ) : status === "done"
                                        ? "Completed ✓"
                                        : status === "missed"
                                        ? "Mark as Complete (Missed)"
                                        : "Mark Complete"}
                                    </CompleteBtn>

                                    <EditorBtn
                                      onClick={() =>
                                        navigate(`/roadmap/day?dayId=${day.id}`)
                                      }
                                    >
                                      ▶ View Full Day
                                    </EditorBtn>

                                    <EditorBtn
                                      onClick={() =>
                                        navigate(
                                          `/tasks-board/day?dayId=${day.id}&roadmapId=${roadmapMeta.roadmapMetaId}`
                                        )
                                      }
                                    >
                                      🧩 View Task Board
                                    </EditorBtn>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </DayCard>
                          );
                        })}
                    </WeekCard>
                  );
                })}

              {/* PHASE QUESTIONS */}
              {openPhase === pIdx && phase.questions?.length > 0 && (
                <div style={{ marginTop: 28 }}>
                  <SectionTitle>
                    Phase Interview & Reflection Questions
                  </SectionTitle>

                  {phase.questions.map((q, qi) => (
                    <QuestionCard key={q.id}>
                      <QuestionTitle>
                        Q{qi + 1}. {q.question}
                      </QuestionTitle>

                      <QuestionMeta>
                        <QuestionPill>🎯 {q.difficulty}</QuestionPill>
                        {q.topics?.map((t, i) => (
                          <QuestionPill key={i}>{t}</QuestionPill>
                        ))}
                      </QuestionMeta>
                    </QuestionCard>
                  ))}
                </div>
              )}
            </AnimatePresence>
          </PhaseCard>
        );
      })}

      {showReminder && (
        <Overlay onClick={() => setShowReminder(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            {/* HEADER */}
            <ModalHeader>
              <TitleRow>
                <IconCircle>⏰</IconCircle>
                <h3>Reminder Settings</h3>
              </TitleRow>
              <CloseBtn onClick={() => setShowReminder(false)}>✕</CloseBtn>
            </ModalHeader>

            {/* BODY */}
            <ModalBody>
              <SettingRow>
                <SettingText>
                  <h4>Email Reminder</h4>
                  <p>Get a daily reminder every morning</p>
                </SettingText>

                <Toggle
                  checked={emailReminderEnabled}
                  onChange={() => setEmailReminderEnabled((v) => !v)}
                />
              </SettingRow>

              <Divider />

              {/* CALENDAR REMINDER */}
              <SettingRow>
                <SettingText>
                  <h4>Google Calendar Reminder</h4>
                  <p>Get notified at your preferred time</p>
                </SettingText>

                <Toggle
                  checked={calendarReminderEnabled}
                  onChange={() => setCalendarReminderEnabled((v) => !v)}
                />
              </SettingRow>

              {/* CALENDAR TIME PICKER */}
              {calendarReminderEnabled && (
                <>
                  <Label style={{ marginTop: 16 }}>Choose reminder time</Label>

                  <TimeInput
                    type="time"
                    value={calendarTime}
                    onChange={(e) => setCalendarTime(e.target.value)}
                  />

                  <Hint>
                    Default time is <strong>07:30 AM</strong>. You can change
                    this anytime.
                  </Hint>

                  <InfoBox style={{ marginTop: 14 }}>
                    {calendarConnected ? (
                      <>
                        ✅ Google Calendar is connected. Your roadmap events can
                        now sync to the same Google account.
                      </>
                    ) : (
                      <>
                        Google Calendar is not connected yet. After saving, you
                        will be redirected to Google to grant calendar access.
                      </>
                    )}
                  </InfoBox>

                  {!calendarConnected && (
                    <Save
                      style={{ marginTop: 14, width: "100%" }}
                      onClick={() => {
                        window.location.href = getGoogleAuthUrl();
                      }}
                    >
                      Connect Google Calendar
                    </Save>
                  )}
                </>
              )}

              {/* INFO */}
              <InfoBox>
                Email reminders are sent automatically every morning. Calendar
                reminders use your selected time.
              </InfoBox>
            </ModalBody>

            {/* FOOTER */}
            <ModalFooter>
              <Cancel onClick={() => setShowReminder(false)}>Cancel</Cancel>

              <Save onClick={saveReminderTime} disabled={saving}>
                {saving ? "Saving..." : "Save Settings"}
              </Save>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}
      {showCalendarSuccess && (
        <Overlay onClick={() => setShowCalendarSuccess(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <TitleRow>
                <IconCircle>📅</IconCircle>
                <h3>Calendar Reminder Set</h3>
              </TitleRow>
              <CloseBtn onClick={() => setShowCalendarSuccess(false)}>
                ✕
              </CloseBtn>
            </ModalHeader>

            <ModalBody>
              <p style={{ fontSize: 14, lineHeight: 1.6 }}>
                🎉 Your Google Calendar reminder has been set successfully.
              </p>

              <p style={{ marginTop: 10, fontSize: 14, opacity: 0.85 }}>
                Please check the <strong>same Google account</strong> you used
                while connecting calendar.
              </p>

              <InfoBox>
                ⏳ Calendar events may take a few minutes to appear. If you
                don’t see them immediately, don’t worry — they’re on the way.
              </InfoBox>
            </ModalBody>

            <ModalFooter>
              <Save onClick={() => setShowCalendarSuccess(false)}>
                Got it 👍
              </Save>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}
      {showShareProgress && shareData && (
        <Overlay onClick={() => setShowShareProgress(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <TitleRow>
                <IconCircle>🎉</IconCircle>
                <h3>Small win unlocked!</h3>
              </TitleRow>
              <CloseBtn onClick={() => setShowShareProgress(false)}>✕</CloseBtn>
            </ModalHeader>

            <ModalBody>
              {/* SYSTEM GENERATED CARD */}
              <div
                style={{
                  padding: 14,
                  borderRadius: 14,
                  background:
                    "linear-gradient(135deg, rgba(99,102,241,0.22), rgba(99,102,241,0.08))",
                  border: "1px solid rgba(99,102,241,0.45)",
                }}
              >
                <strong>🚀 Progress Update</strong>
                <div style={{ marginTop: 6, fontSize: 14 }}>
                  Day {shareData.dayNumber} completed — {shareData.dayTitle}
                </div>
                <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>
                  ✔ Verified from roadmap
                </div>
              </div>

              {/* OPTIONAL REFLECTION */}
              <Label style={{ marginTop: 16 }}>Reflection (optional)</Label>

              <textarea
                placeholder="What was challenging or interesting today?"
                value={reflection}
                onChange={(e) => setReflection(e.target.value)}
                style={{
                  width: "100%",
                  minHeight: 80,
                  marginTop: 6,
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 12,
                  padding: 12,
                  color: "#f8fafc",
                }}
              />

              <InfoBox>
                Learning publicly helps you stay consistent and motivated.
              </InfoBox>
            </ModalBody>

            <ModalFooter>
              <Cancel onClick={() => setShowShareProgress(false)}>
                Skip for now
              </Cancel>

              <Save
                disabled={sharing}
                onClick={async () => {
                  try {
                    setSharing(true);

                    await addDoc(collection(db, "community_posts"), {
                      uid: user.uid,
                      userName: user.name || "Anonymous",

                      postType: "progress",
                      systemGenerated: true,

                      roadmapRef: {
                        roadmapId: shareData.roadmapId,
                        dayId: shareData.dayId,
                        dayTitle: shareData.dayTitle,
                        dayNumber: shareData.dayNumber,
                      },

                      reflection: reflection || null,

                      createdAt: serverTimestamp(),
                      editedAt: null,
                      isDeleted: false,
                    });

                    setShowShareProgress(false);
                  } catch (e) {
                    console.error(e);
                    alert("Failed to share progress. Try again.");
                  } finally {
                    setSharing(false);
                  }
                }}
              >
                {sharing ? "Sharing..." : "🔥 Share in Community"}
              </Save>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}
    </Page>
  );
}
